import { useEffect, useState } from "react"
import Callout from "components/callout/callout"
import FormErrors from "components/formParts/formErrors"
import { useTranslation } from "react-i18next"
import TextInput from "components/inputs/textInput"
import useLocations from "hooks/useLocations"
import useLayout from "hooks/useLayout"
import Form from "components/forms/form"
import { ReactComponent as DeleteIcon } from "assets/icons/delete.svg"
import { ReactComponent as CloseIcon } from "assets/icons/close.svg"
import Button from "components/inputs/button"
import ButtonGroup from "components/buttons/buttonGroup"
import Section from "components/layout/section"
import ProcessingButton from "components/inputs/processingButton"
import { decodeFirebaseError } from "utils/firebaseHelpers"

interface DeleteLocationFormProps {
    storageId: string,
    locationId: string
}

const DeleteLocationForm: React.FC<DeleteLocationFormProps> = ({ storageId, locationId }) => {

    const { t } = useTranslation()

    const [name, setName] = useState(``)
    const [confirmation, setConfirmation] = useState(``)
    const [confirmed, setConfirmed] = useState(false)

    const [errors, setErrors] = useState<string[]>([]);

    const {
        location,
        deleteLocationAsync,
        isDeleting
    } = useLocations({ storageId, locationId })

    const { closeDialog, addToast } = useLayout()

    useEffect(() => {
        if ( !location ) return
        setName(location.name)
    }, [location])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (storageId === undefined || !confirmed) return
        
        try {
            await deleteLocationAsync({ storageId, locationId })
            addToast({ text: t('toastLocationDeleted'), colorScheme: 'green' })
            closeDialog()
        } catch (error: unknown) {
            const errorMessage = decodeFirebaseError(error instanceof Error ? error.message : String(error))
            setErrors([errorMessage])
        }
    }

    const handleConfirmationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmation(e.target.value)
        setConfirmed(e.target.value === name)
    }

    if (!storageId) return <Callout type={'error'}>{t('storageIdMissing')}</Callout>

    return (
        <Form
            onSubmit={handleSubmit}
            className={`group`}
            isLoading={isDeleting}
        >
            <Section>
                <p>{t('deleteLocationConfirmation', { name })}</p>
                <TextInput label={t('typeLocationNameToConfirm')} value={confirmation} onChange={handleConfirmationChange} placeholder={name} />
                <ButtonGroup>
                    <Button wideOnMobile type="button" design="optional" disabled={isDeleting} onClick={closeDialog}><CloseIcon /><span>{t('buttonCancel')}</span></Button>
                    {isDeleting ? (
                        <ProcessingButton design="danger" />
                    ) : (
                        <Button wideOnMobile type="submit" design="danger" disabled={!confirmed}><DeleteIcon /><span>{t('buttonDelete')}</span></Button>
                    )}
                </ButtonGroup>
                {errors.length > 0 && <FormErrors errors={errors} />}
            </Section>
        </Form>
    )
}

export default DeleteLocationForm
